<template>
  <v-container grid-list-xs>
    <v-card max-width="640" class="mx-auto">
      <v-card-title class="d-flex align-center flex-wrap ga-2">
        <v-btn icon variant="text" aria-label="Voltar" @click="goBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span>Validar ingresso</span>
      </v-card-title>
      <v-card-text v-if="!groupSelected">Selecione um Grupo</v-card-text>
      <template v-else>
        <v-alert type="info" variant="tonal" density="compact" class="mb-4">
          Consulta e check-in de ingressos emitidos. A validação exige permissão de
          gerente, validador de ingresso ou admin do grupo (API PDV).
        </v-alert>
        <v-form @submit.prevent="doLookup">
          <v-text-field
            v-model="code"
            label="Código do ingresso"
            placeholder="GGT…"
            density="comfortable"
            clearable
            autofocus
            hide-details="auto"
            :disabled="busy"
            @keydown.enter.prevent="doLookup"
          >
            <template v-slot:append-inner>
              <v-btn
                icon
                variant="text"
                :loading="lookingUp"
                :disabled="busy || !codeTrimmed"
                aria-label="Consultar"
                @click="doLookup"
              >
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </v-form>

        <v-card v-if="ticket" variant="outlined" class="mt-4">
          <v-card-text>
            <div class="d-flex align-center flex-wrap ga-2 mb-3">
              <code class="text-h6">{{ ticket.code }}</code>
              <v-chip size="small" variant="tonal" :color="statusColor(ticket.status)">
                {{ statusLabel(ticket.status) }}
              </v-chip>
            </div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="text-caption text-medium-emphasis">Produto</div>
                <div>{{ ticket.product_name || '—' }}</div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="text-caption text-medium-emphasis">Scans</div>
                <div>{{ ticket.scan_count ?? 0 }} / {{ ticket.max_scans ?? 1 }}</div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="text-caption text-medium-emphasis">Venda</div>
                <div>#{{ ticket.sale_id || '—' }}</div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="text-caption text-medium-emphasis">Loja</div>
                <div>#{{ ticket.store_id || '—' }}</div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="text-caption text-medium-emphasis">Emitido</div>
                <div>{{ formatDateTime(ticket.issued_at) }}</div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="text-caption text-medium-emphasis">Usado em</div>
                <div>{{ formatDateTime(ticket.used_at) }}</div>
              </v-col>
              <v-col v-if="ticket.valid_from || ticket.valid_until" cols="12">
                <div class="text-caption text-medium-emphasis">Validade</div>
                <div>
                  {{ formatDateTime(ticket.valid_from) }}
                  →
                  {{ formatDateTime(ticket.valid_until) }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn variant="text" :disabled="busy" @click="clearResult">Limpar</v-btn>
            <v-btn
              color="success"
              :loading="validating"
              :disabled="busy || !canValidate"
              @click="doValidate"
            >
              <v-icon class="mr-1">mdi-check-decagram</v-icon>
              Confirmar check-in
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-alert
          v-else-if="lookupDone && !ticket && !lookingUp"
          type="warning"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          Ingresso não encontrado ou sem permissão para consultar.
        </v-alert>
      </template>
    </v-card>
  </v-container>
</template>
<script>
import api from '@/services'

const STATUS_LABELS = {
  valid: 'Válido',
  used: 'Utilizado',
  voided: 'Anulado',
}
const STATUS_COLORS = {
  valid: 'success',
  used: 'warning',
  voided: 'secondary',
}

export default {
  props: {
    user: Object,
  },
  data() {
    return {
      code: '',
      ticket: null,
      lookingUp: false,
      validating: false,
      lookupDone: false,
    }
  },
  computed: {
    groupSelected() {
      return api.storage.isGroupSelected()
    },
    codeTrimmed() {
      return String(this.code || '').trim()
    },
    busy() {
      return this.lookingUp || this.validating
    },
    canValidate() {
      if (!this.ticket) return false
      if (this.ticket.status === 'voided') return false
      if (this.ticket.status === 'used') {
        const max = Number(this.ticket.max_scans || 1)
        const scans = Number(this.ticket.scan_count || 0)
        return scans < max
      }
      return this.ticket.status === 'valid'
    },
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push({ name: 'Tickets' })
      }
    },
    statusLabel(value) {
      return STATUS_LABELS[value] || value || '—'
    },
    statusColor(value) {
      return STATUS_COLORS[value] || 'default'
    },
    formatDateTime(value) {
      if (!value) return '—'
      if (this.$moment) {
        return this.$moment(value).format('DD/MM/YYYY HH:mm')
      }
      try {
        return new Date(value).toLocaleString('pt-BR')
      } catch {
        return value
      }
    },
    clearResult() {
      this.ticket = null
      this.lookupDone = false
      this.code = ''
    },
    doLookup() {
      const vm = this
      const code = vm.codeTrimmed
      if (!code || vm.busy) return
      vm.lookingUp = true
      vm.lookupDone = false
      vm.ticket = null
      return api.ticket
        .lookup(code)
        .then((result) => {
          vm.ticket = result
          vm.lookupDone = true
          if (!result) {
            vm.$notify({ type: 'warn', text: 'Ingresso não encontrado' })
          }
        })
        .catch((err) => {
          vm.ticket = null
          vm.lookupDone = true
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.lookingUp = false
        })
    },
    doValidate() {
      const vm = this
      const code = vm.ticket?.code || vm.codeTrimmed
      if (!code || vm.busy || !vm.canValidate) return
      vm.validating = true
      return api.ticket
        .validate(code)
        .then((result) => {
          vm.ticket = result || vm.ticket
          vm.$notify({ type: 'success', text: 'Check-in confirmado' })
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.validating = false
        })
    },
  },
}
</script>
